import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import type { OdProtosSellSellerData_SellerExperience as SellerExperience } from '__generated__/graphql';
import { DevError } from 'platform/Error';

type SellerJourneyNonHubRoutingDecision = {
  lifecycle: 'denied' | 'initializing' | 'withdrawn';
  pageStep: null;
  sellerExperience: SellerExperience;
};

type SellerJourneyPreContractRoutingDecision = {
  lifecycle: 'pre-contract';
  pageStep: 'assessment' | 'contract-signing';
  sellerExperience: SellerExperience;
};

type SellerJourneyInContractRoutingDecision = {
  lifecycle: 'in-contract';
  pageStep: 'closing' | 'repricing';
  sellerExperience: SellerExperience;
};

type SellerJourneyClosedRoutingDecision = {
  lifecycle: 'closed';
  pageStep: 'closed';
  sellerExperience: SellerExperience;
};

export type SellerJourneyRoutingDecision =
  | SellerJourneyClosedRoutingDecision
  | SellerJourneyInContractRoutingDecision
  | SellerJourneyNonHubRoutingDecision
  | SellerJourneyPreContractRoutingDecision;

const SellerJourneyRoutingContext = createContext<SellerJourneyRoutingDecision | null | undefined>(
  undefined,
);

export const SellerJourneyRoutingProvider: FC<
  PropsWithChildren<{ decision: SellerJourneyRoutingDecision | null }>
> = ({ children, decision }) => (
  <SellerJourneyRoutingContext.Provider value={decision}>
    {children}
  </SellerJourneyRoutingContext.Provider>
);

export const useSellerJourneyRoutingDecision = (): SellerJourneyRoutingDecision | null => {
  const decision = useContext(SellerJourneyRoutingContext);
  if (decision === undefined) {
    throw new DevError(
      'useSellerJourneyRoutingDecision must be used inside SellerJourneyRoutingProvider',
    );
  }
  return decision;
};
